<template>
  <div class="testador-regra">
    <button
      type="button"
      class="testador-toggle"
      :class="{ 'testador-toggle--aberto': aberto }"
      @click="aberto = !aberto"
    >
      <fa-icon :icon="aberto ? 'chevron-down' : 'chevron-right'" />
      <span class="testador-toggle-label">Testar esta regra</span>
      <span v-if="resultado && !aberto" class="testador-badge">
        <fa-icon icon="check-circle" v-if="!erroAvaliacao" /> resultado disponível
      </span>
    </button>

    <div v-if="aberto" class="testador-corpo">
      <div class="testador-ajuda">
        <fa-icon icon="info-circle" />
        <div>
          <strong>Como funciona:</strong> o JSON abaixo simula o objeto que o sistema receberia em runtime.
          Os campos da regra (ex: <code>{{ campoExemplo }}</code>) leem do JSON via caminho com pontos.
          <span v-if="caminhosDetectados.length">
            <br />
            <strong>Campos referenciados nesta regra:</strong>
            <code v-for="c in caminhosDetectados" :key="c" class="caminho-tag">{{ c }}</code>
          </span>
        </div>
      </div>

      <div class="testador-grid">
        <div class="testador-col">
          <label class="field-label">JSON de entrada</label>
          <textarea
            ref="payloadTextarea"
            class="form-control form-control-sm payload-input"
            :class="{ 'is-invalid': erroJson }"
            v-model="payloadTexto"
            spellcheck="false"
            :placeholder="placeholderJson"
            @input="resultado = null"
          ></textarea>
          <div v-if="erroJson" class="invalid-feedback d-block">
            {{ erroJson }}
          </div>
          <div class="testador-sugestoes" v-if="exemplosEfetivos.length">
            <span class="sugestoes-label">Carregar exemplo:</span>
            <CButton
              v-for="(ex, i) in exemplosEfetivos"
              :key="i"
              color="secondary"
              variant="outline"
              size="sm"
              class="sugestao-btn"
              @click="usarExemplo(ex)"
            >
              {{ ex.label }}
            </CButton>
          </div>
          <CButton
            color="primary"
            size="sm"
            class="testador-avaliar"
            @click="avaliar"
          >
            <fa-icon icon="play" /> Avaliar
          </CButton>
        </div>

        <div class="testador-col">
          <label class="field-label">Resultado</label>
          <div v-if="!resultado" class="testador-empty">
            Clique em "Avaliar" para ver o resultado da regra com o JSON de entrada.
          </div>
          <div v-else-if="erroAvaliacao" class="testador-erro">
            <fa-icon icon="exclamation-triangle" /> {{ erroAvaliacao }}
          </div>
          <div v-else class="testador-resultado">
            <div class="resultado-matched" :class="matchedClass">
              <fa-icon :icon="matchedIcon" />
              <span>{{ matchedLabel }}</span>
            </div>
            <div v-if="alertaFalsy" class="alerta-falsy">
              <fa-icon icon="exclamation-triangle" />
              <span>
                A regra retornou <code>{{ alertaFalsy }}</code>, mas no envio ao ERP isso vira o <strong>valor padrão</strong>
                (que está vazio). Defina o "Valor padrão" em opções avançadas se precisar enviar <code>{{ alertaFalsy }}</code> literal.
              </span>
            </div>
            <div class="resultado-output">
              <div v-if="resultado.multi" class="resultado-multi">
                <div
                  v-for="(item, i) in resultado.itens"
                  :key="i"
                  class="resultado-saida"
                >
                  <code class="saida-para">{{ item.para || '(sem destino)' }}</code>
                  <fa-icon icon="arrow-right" class="saida-seta" />
                  <code class="saida-valor">{{ formatValor(item.valor) }}</code>
                </div>
              </div>
              <code v-else class="resultado-valor-unico">{{ formatValor(resultado.valor) }}</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { avaliarRegra } from "./avaliadorRegra";
import { gerarExemplosParaRegra } from "./gerarExemplos";

export default {
  name: "TestadorRegra",
  props: {
    regra: { type: Object, default: null },
    exemplos: { type: Array, default: () => [] },
  },
  data() {
    return {
      aberto: false,
      payloadTexto: "{}",
      resultado: null,
      erroJson: "",
      erroAvaliacao: "",
    };
  },
  computed: {
    exemplosGerados() {
      return gerarExemplosParaRegra(this.regra);
    },
    exemplosEfetivos() {
      return this.exemplos && this.exemplos.length ? this.exemplos : this.exemplosGerados;
    },
    caminhosDetectados() {
      // extrai de todos os exemplos os caminhos top-level (sem duplicar)
      const set = new Set();
      function walk(obj, prefix) {
        if (!obj || typeof obj !== "object") return;
        Object.keys(obj).forEach((k) => {
          const novoPrefix = prefix ? `${prefix}.${k}` : k;
          if (obj[k] && typeof obj[k] === "object" && !Array.isArray(obj[k])) {
            walk(obj[k], novoPrefix);
          } else {
            set.add(novoPrefix);
          }
        });
      }
      this.exemplosEfetivos.forEach((ex) => walk(ex.payload, ""));
      return [...set];
    },
    placeholderJson() {
      if (this.exemplosEfetivos.length > 0) {
        return JSON.stringify(this.exemplosEfetivos[0].payload, null, 2);
      }
      return '{"cliente": {"uf": "SP"}}';
    },
    campoExemplo() {
      return this.caminhosDetectados[0] || "cliente.uf";
    },
    alertaFalsy() {
      // detecta quando o resultado é false/0/"" que viraria valor_padrao no envio real
      if (!this.resultado || this.resultado.multi) return null;
      const v = this.resultado.valor;
      if (v === false) return "false";
      if (v === 0) return "0";
      if (v === "") return '""';
      return null;
    },
    matchedClass() {
      if (!this.resultado) return "";
      const t = this.resultado.matched.tipo;
      if (t === "caso" || t === "mapeado" || t === "campo" || t === "concat") return "matched--success";
      if (t === "senao" || t === "padrao") return "matched--fallback";
      if (t === "erro") return "matched--erro";
      return "matched--neutral";
    },
    matchedIcon() {
      if (!this.resultado) return "info-circle";
      const t = this.resultado.matched.tipo;
      if (t === "caso") return "check-circle";
      if (t === "senao" || t === "padrao") return "level-down-alt";
      if (t === "erro") return "exclamation-triangle";
      if (t === "nenhum" || t === "vazio") return "ban";
      return "info-circle";
    },
    matchedLabel() {
      if (!this.resultado) return "";
      const m = this.resultado.matched;
      switch (m.tipo) {
        case "caso":    return `Regra ${m.index + 1} casou`;
        case "senao":   return "Nenhuma regra casou — usando fallback (senão)";
        case "nenhum":  return "Nenhuma regra casou — sem fallback definido, retornando null";
        case "campo":   return `Coalesce: campo #${m.index + 1} (${m.caminho}) tinha valor`;
        case "concat":  return `Concatenação: ${m.partes} parte(s) unidas`;
        case "mapeado": return `Mapeamento: chave "${m.chave}" encontrada no mapa`;
        case "padrao":  return m.chave != null
          ? `Mapeamento: chave "${m.chave}" não encontrada — usando padrão`
          : "Mapeamento: campo não definido — usando padrão";
        case "vazio":   return "Regra vazia — retornando null";
        case "erro":    return `Erro: ${m.erro}`;
        default:        return "Avaliado";
      }
    },
  },
  methods: {
    avaliar() {
      this.erroJson = "";
      this.erroAvaliacao = "";
      this.resultado = null;
      let objeto;
      try {
        objeto = this.payloadTexto.trim() ? JSON.parse(this.payloadTexto) : {};
      } catch (e) {
        this.erroJson = `JSON inválido: ${e.message}`;
        return;
      }
      const r = avaliarRegra(objeto, this.regra);
      if (r.matched.tipo === "erro") {
        this.erroAvaliacao = r.matched.erro;
        this.resultado = r;
        return;
      }
      if (Array.isArray(r.valor) && r.valor.length && r.valor[0] && "para" in r.valor[0]) {
        this.resultado = { ...r, multi: true, itens: r.valor };
      } else {
        this.resultado = { ...r, multi: false };
      }
    },
    usarExemplo(ex) {
      this.payloadTexto = JSON.stringify(ex.payload, null, 2);
      this.resultado = null;
      this.$nextTick(() => {
        if (this.$refs.payloadTextarea) this.$refs.payloadTextarea.focus();
      });
    },
    formatValor(v) {
      if (v === null || v === undefined) return "null";
      if (typeof v === "string") return `"${v}"`;
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
    },
  },
};
</script>

<style scoped>
.testador-regra {
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--bg-subtle);
  margin-top: 12px;
}
.testador-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: transparent;
  border: none;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-strong);
  text-align: left;
  cursor: pointer;
  border-radius: 8px 8px 0 0;
}
.testador-toggle:hover { background: rgba(58, 62, 129, 0.04); }
.testador-toggle--aberto { border-bottom: 1px solid var(--border-default); }
.testador-toggle-label { flex: 1; }
.testador-badge {
  margin-left: auto;
  font-size: 11px;
  font-weight: 400;
  color: var(--text-subtle);
}
.testador-corpo {
  padding: 0 12px 12px;
}
.testador-ajuda {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: var(--text-strong);
  background: #eef4ff;
  border: 1px solid #c7d8f7;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 8px 0 12px;
  line-height: 1.5;
}
.testador-ajuda ::v-deep > svg { color: #2563eb; flex-shrink: 0; margin-top: 2px; }
.testador-ajuda code {
  background: #fff;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
  color: var(--text-strong);
  border: 1px solid #c7d8f7;
}
.caminho-tag {
  display: inline-block;
  margin: 2px 4px 0 0;
}
.testador-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
  display: block;
}
.payload-input {
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  min-height: 120px;
  resize: vertical;
}
.testador-sugestoes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 11px;
}
.sugestoes-label {
  color: var(--text-muted);
  margin-right: 4px;
}
.sugestao-btn { font-size: 11px !important; }
.testador-avaliar { margin-top: 8px; }
.testador-empty {
  font-size: 12px;
  color: var(--text-subtle);
  padding: 12px;
  border: 1px dashed var(--border-default);
  border-radius: 6px;
  background: #fff;
}
.testador-erro {
  font-size: 12px;
  color: var(--danger);
  background: var(--danger-bg);
  padding: 8px 12px;
  border-radius: 6px;
}
.alerta-falsy {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 11px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 8px 10px;
  margin: 6px 0;
  line-height: 1.5;
}
.alerta-falsy code {
  background: #fff;
  padding: 1px 4px;
  border-radius: 3px;
  color: var(--text-strong);
  border: 1px solid #fde68a;
}
.testador-resultado {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.resultado-matched {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 10px;
  border-radius: 6px;
}
.matched--success { background: #ecfdf5; color: #047857; }
.matched--fallback { background: #fff7ed; color: #c2410c; }
.matched--erro { background: var(--danger-bg); color: var(--danger); }
.matched--neutral { background: var(--bg-subtle); color: var(--text-muted); }
.resultado-output { font-family: ui-monospace, Menlo, monospace; font-size: 12px; }
.resultado-valor-unico {
  display: inline-block;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  color: var(--text-strong);
  word-break: break-all;
}
.resultado-multi {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.resultado-saida {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
}
.saida-para, .saida-valor {
  padding: 2px 6px;
  background: #fff;
  border: 1px solid var(--border-default);
  border-radius: 4px;
  color: var(--text-strong);
}
.saida-seta { color: var(--text-subtle); }

@media (max-width: 767.98px) {
  .testador-grid { grid-template-columns: 1fr; }
}
</style>
